import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Bus as BusIcon, Users, MapPin, Edit, Trash2, UserCheck } from "lucide-react";

const Transport = () => {
  const [buses, setBuses] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]); // Add classes state
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isBusViewOpen, setIsBusViewOpen] = useState(false);
  const [isAssignTeacherOpen, setIsAssignTeacherOpen] = useState(false);
  const [selectedBus, setSelectedBus] = useState(null);
  const [editingBus, setEditingBus] = useState(null);
  const [activeTab, setActiveTab] = useState("students");
  
  const [teacherAssignForm, setTeacherAssignForm] = useState({
    teacher_id: "",
    session: "morning",
    role: "supervisor"
  });

  const [busForm, setBusForm] = useState({
    bus_name: "",
    plate_number: "",
    driver_name: "",
    capacity: "",
    area_served: "",
    status: "active",
    notes: "",
    assigned_students: [],
    assigned_teachers: [],
  });

  const [editForm, setEditForm] = useState({
    bus_name: "",
    plate_number: "",
    driver_name: "",
    capacity: "",
    area_served: "",
    status: "active",
    notes: "",
    assigned_students: [],
    assigned_teachers: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (retryCount = 0) => {
    try {
      const [busesRes, studentsRes, teachersRes, classesRes] = await Promise.all([
        axios.get(`${API}/buses`),
        axios.get(`${API}/students`),
        axios.get(`${API}/teachers`),
        axios.get(`${API}/classes`),
      ]);
      setBuses(busesRes.data || []);
      setStudents(studentsRes.data || []);
      setTeachers(teachersRes.data || []);
      setClasses(classesRes.data || []);
      setLoading(false);
    } catch (error) {
      console.error("Failed to load transport data:", error);
      
      // Retry once after 3 seconds
      if (retryCount === 0) {
        setTimeout(() => {
          fetchData(1);
        }, 3000);
      } else {
        toast.error("⚠️ Unable to load transport data. Please check connection.");
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/buses`, {
        ...busForm,
        capacity: parseInt(busForm.capacity),
      });
      toast.success("✅ Bus added successfully!");
      setIsDialogOpen(false);
      resetForm();
      fetchData(); // Auto-refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add bus");
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/buses/${editingBus.id}`, {
        ...editForm,
        capacity: parseInt(editForm.capacity),
      });
      toast.success("✅ Bus updated successfully!");
      setIsEditDialogOpen(false);
      setEditingBus(null);
      fetchData(); // Auto-refresh
    } catch (error) {
      toast.error("Failed to update bus");
    }
  };

  const handleDelete = async (busId) => {
    if (!window.confirm("Are you sure you want to delete this bus?")) {
      return;
    }

    try {
      await axios.delete(`${API}/buses/${busId}`);
      toast.success("❌ Bus deleted");
      fetchData(); // Auto-refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete bus");
    }
  };

  const openEditDialog = (bus) => {
    setEditingBus(bus);
    setEditForm({
      bus_name: bus.bus_name,
      plate_number: bus.plate_number,
      driver_name: bus.driver_name,
      capacity: bus.capacity.toString(),
      area_served: bus.area_served,
      status: bus.status,
      notes: bus.notes || "",
      assigned_students: bus.assigned_students || [],
      assigned_teachers: bus.assigned_teachers || [],
    });
    setIsEditDialogOpen(true);
  };

  const openBusView = async (bus) => {
    setSelectedBus(bus);
    setActiveTab("students");
    setIsBusViewOpen(true);
    
    // Refresh data to ensure consistency when opening bus view
    await fetchData();
    setForceUpdate(prev => prev + 1);
  };

  const getAssignedStudents = (busId) => {
    return students.filter(s => s.assigned_bus_id === busId);
  };

  // Add a state variable to force re-renders
  const [forceUpdate, setForceUpdate] = useState(0);
  
  const getAssignedTeachers = (busId) => {
    const assigned = teachers.filter(teacher => teacher.bus_assigned === busId);
    console.log(`Bus ${busId} assigned teachers:`, assigned); // Debug log
    return assigned;
  };

  // Helper function to get class name from class_id
  const getClassName = (classId) => {
    const classObj = classes.find(cls => cls.id === classId);
    return classObj ? classObj.name : 'Unknown Class';
  };

  const handleAssignTeacher = async (e) => {
    e.preventDefault();
    if (!teacherAssignForm.teacher_id || !selectedBus) {
      toast.error("Please select a teacher");
      return;
    }

    try {
      console.log('Assigning teacher:', teacherAssignForm.teacher_id, 'to bus:', selectedBus.id); // Debug log

      // Update teacher with bus assignment
      const response = await axios.put(`${API}/teachers/${teacherAssignForm.teacher_id}`, {
        bus_assigned: selectedBus.id,
        bus_session: teacherAssignForm.session,
        bus_role: teacherAssignForm.role
      });

      console.log('Assignment response:', response.data); // Debug log

      // Update local state immediately for instant UI feedback
      if (response.data) {
        const updatedTeacher = response.data;
        setTeachers(prev => {
          const newTeachers = prev.map(teacher => 
            teacher.id === teacherAssignForm.teacher_id 
              ? { ...teacher, ...updatedTeacher }
              : teacher
          );
          console.log('Updated teachers state:', newTeachers); // Debug log
          return newTeachers;
        });
        
        // Force multiple re-render triggers
        setForceUpdate(prev => prev + 1);
        setSelectedBus(prev => ({ ...prev, last_updated: Date.now() }));
      }

      toast.success("✅ Teacher assigned to bus successfully");
      setIsAssignTeacherOpen(false);
      setTeacherAssignForm({
        teacher_id: "",
        session: "morning",
        role: "supervisor"
      });
      
      // Also refresh data after assignment to ensure backend consistency
      setTimeout(async () => {
        console.log('Fetching updated data...'); // Debug log
        await fetchData();
        setForceUpdate(prev => prev + 1);
      }, 500);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to assign teacher";
      toast.error(errorMessage);
      console.error(error);
    }
  };

  const handleRemoveTeacherFromBus = async (teacherId) => {
    if (!window.confirm("Are you sure you want to remove this teacher from the bus?")) return;

    try {
      // Remove bus assignment from teacher
      const response = await axios.put(`${API}/teachers/${teacherId}`, {
        bus_assigned: null,
        bus_session: null,
        bus_role: null
      });

      // Update local state immediately for instant UI feedback
      if (response.data) {
        const updatedTeacher = response.data;
        setTeachers(prev => prev.map(teacher => 
          teacher.id === teacherId 
            ? { ...teacher, ...updatedTeacher }
            : teacher
        ));
        
        // Force a re-render by updating selected bus
        setSelectedBus(prev => ({ ...prev, last_updated: Date.now() }));
      }

      toast.success("✅ Teacher removed from bus");
      
      // Refresh data to ensure backend consistency
      setTimeout(async () => {
        await fetchData();
        // Force re-render after data fetch
        setSelectedBus(prev => ({ ...prev, last_updated: Date.now() }));
      }, 300);
    } catch (error) {
      toast.error("Failed to remove teacher");
      console.error(error);
    }
  };

  const getStudentCount = (busId) => {
    return students.filter(s => s.assigned_bus_id === busId).length;
  };

  const resetForm = () => {
    setBusForm({
      bus_name: "",
      plate_number: "",
      driver_name: "",
      capacity: "",
      area_served: "",
      status: "active",
      notes: "",
      assigned_students: [],
      assigned_teachers: [],
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">🟢 Active</span>,
      maintenance: <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">🔧 Maintenance</span>,
      retired: <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">⚪ Retired</span>,
    };
    return badges[status] || badges.active;
  };

  const calculateStats = () => {
    const totalBuses = buses.length;
    const studentsUsingBus = students.filter(s => s.transport_type === "bus").length;
    const serviceAreas = [...new Set(buses.map(b => b.area_served))].length;
    const busesInUse = buses.filter(b => students.some(s => s.assigned_bus_id === b.id)).length;

    return { totalBuses, studentsUsingBus, serviceAreas, busesInUse };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="transport-page">
      <div>
        <h2 className="text-2xl font-bold text-[#1E624A]">Transport Management</h2>
        <p className="text-sm text-gray-600">Maamulka Gaadiidka</p>
      </div>

      {/* Summary Cards */}
      <div className="compact-summary-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="compact-summary-card">
          <span className="card-icon">🚌</span>
          <div className="card-content">
            <span className="card-count">{stats.totalBuses}</span>
            <div className="card-label">Total Buses</div>
            <div className="card-sublabel">Wadarta Baasaska</div>
          </div>
        </div>
        
        <div className="compact-summary-card">
          <span className="card-icon">🚍</span>
          <div className="card-content">
            <span className="card-count">{stats.busesInUse}</span>
            <div className="card-label">Buses in Use</div>
            <div className="card-sublabel">Baasas La Isticmaalo</div>
          </div>
        </div>
        
        <div className="compact-summary-card">
          <span className="card-icon">👥</span>
          <div className="card-content">
            <span className="card-count">{stats.studentsUsingBus}</span>
            <div className="card-label">Students Using Bus</div>
            <div className="card-sublabel">Ardayda Baaska Iska</div>
          </div>
        </div>
        
        <div className="compact-summary-card">
          <span className="card-icon">📍</span>
          <div className="card-content">
            <span className="card-count">{stats.serviceAreas}</span>
            <div className="card-label">Service Areas</div>
            <div className="card-sublabel">Meelaha Adeegga</div>
          </div>
        </div>
      </div>

      {/* Add Bus Button */}
      <div className="flex justify-end">
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-bus-btn"
              onClick={resetForm}
              className="bg-[#1E624A] hover:bg-[#164935]"
            >
              <Plus size={20} className="mr-2" />
              Add Bus
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[85vh] flex flex-col p-0 gap-0">
            <DialogHeader className="px-6 pt-6 pb-4 border-b">
              <DialogTitle className="text-xl text-[#1E624A]">Add New Bus</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="overflow-y-auto px-6 py-4 space-y-4 flex-1">
                <div className="space-y-2">
                  <Label htmlFor="bus_name" className="text-sm font-medium">Bus Name/Number *</Label>
                  <Input
                    id="bus_name"
                    value={busForm.bus_name}
                    onChange={(e) => setBusForm({ ...busForm, bus_name: e.target.value })}
                    required
                    placeholder="Bus 101"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="plate_number" className="text-sm font-medium">Plate Number *</Label>
                  <Input
                    id="plate_number"
                    value={busForm.plate_number}
                    onChange={(e) => setBusForm({ ...busForm, plate_number: e.target.value })}
                    required
                    placeholder="ABC-1234"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="driver_name" className="text-sm font-medium">Driver Name *</Label>
                  <Input
                    id="driver_name"
                    value={busForm.driver_name}
                    onChange={(e) => setBusForm({ ...busForm, driver_name: e.target.value })}
                    required
                    placeholder="Mohamed Ahmed"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="capacity" className="text-sm font-medium">Capacity *</Label>
                  <Input
                    id="capacity"
                    type="number"
                    min="1"
                    value={busForm.capacity}
                    onChange={(e) => setBusForm({ ...busForm, capacity: e.target.value })}
                    required
                    placeholder="40"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="area_served" className="text-sm font-medium">Area Served *</Label>
                  <Input
                    id="area_served"
                    value={busForm.area_served}
                    onChange={(e) => setBusForm({ ...busForm, area_served: e.target.value })}
                    required
                    placeholder="Downtown Area"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status" className="text-sm font-medium">Status *</Label>
                  <Select
                    value={busForm.status}
                    onValueChange={(value) => setBusForm({ ...busForm, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">🟢 Active</SelectItem>
                      <SelectItem value="maintenance">🔧 In Maintenance</SelectItem>
                      <SelectItem value="retired">⚪ Retired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-sm font-medium">Notes</Label>
                  <Textarea
                    id="notes"
                    value={busForm.notes}
                    onChange={(e) => setBusForm({ ...busForm, notes: e.target.value })}
                    placeholder="Route details, timings, etc."
                    rows={3}
                  />
                </div>
              </div>
              <div className="border-t bg-gray-50 px-6 py-4 flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#1E624A] hover:bg-[#164935]">
                  Add Bus
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Buses Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1E624A] border-b-2 border-[#E4C987]">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wider">Bus Name</th>
                <th className="px-4 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wider">Plate Number</th>
                <th className="px-4 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wider">Driver</th>
                <th className="px-4 py-3.5 text-center text-xs font-bold text-white uppercase tracking-wider">Capacity</th>
                <th className="px-4 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wider">Area Served</th>
                <th className="px-4 py-3.5 text-center text-xs font-bold text-white uppercase tracking-wider">Students</th>
                <th className="px-4 py-3.5 text-center text-xs font-bold text-white uppercase tracking-wider">Status</th>
                <th className="px-4 py-3.5 text-center text-xs font-bold text-white uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {buses.map((bus, index) => {
                const studentCount = getStudentCount(bus.id);
                return (
                  <tr 
                    key={bus.id} 
                    className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-[#f2f8f5] transition-colors`}
                  >
                    <td className="px-4 py-4 align-middle">
                      <span className="font-medium text-gray-900">{bus.bus_name}</span>
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <span className="text-gray-700">{bus.plate_number}</span>
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <span className="text-gray-700">{bus.driver_name}</span>
                    </td>
                    <td className="px-4 py-4 text-center align-middle">
                      <span className="font-semibold text-gray-900">{bus.capacity}</span>
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <span className="text-gray-700">{bus.area_served}</span>
                    </td>
                    <td className="px-4 py-4 text-center align-middle">
                      <button
                        onClick={() => openBusView(bus)}
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${
                          studentCount > 0 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {studentCount} student{studentCount !== 1 ? 's' : ''}
                      </button>
                    </td>
                    <td className="px-4 py-4 text-center align-middle">
                      {getStatusBadge(bus.status)}
                    </td>
                    <td className="px-4 py-4 text-center align-middle">
                      <div className="flex gap-2 justify-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(bus)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                          onClick={() => handleDelete(bus.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {buses.length === 0 && !loading && (
            <div className="text-center py-8 text-gray-500">
              No buses found. Add your first bus to get started.
            </div>
          )}
        </div>
      </div>

      {/* Edit Bus Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="text-xl text-[#1E624A]">Edit Bus</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="overflow-y-auto px-6 py-4 space-y-4 flex-1">
              <div className="space-y-2">
                <Label htmlFor="edit_bus_name" className="text-sm font-medium">Bus Name/Number *</Label>
                <Input
                  id="edit_bus_name"
                  value={editForm.bus_name}
                  onChange={(e) => setEditForm({ ...editForm, bus_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_plate_number" className="text-sm font-medium">Plate Number *</Label>
                <Input
                  id="edit_plate_number"
                  value={editForm.plate_number}
                  onChange={(e) => setEditForm({ ...editForm, plate_number: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_driver_name" className="text-sm font-medium">Driver Name *</Label>
                <Input
                  id="edit_driver_name"
                  value={editForm.driver_name}
                  onChange={(e) => setEditForm({ ...editForm, driver_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_capacity" className="text-sm font-medium">Capacity *</Label>
                <Input
                  id="edit_capacity"
                  type="number"
                  min="1"
                  value={editForm.capacity}
                  onChange={(e) => setEditForm({ ...editForm, capacity: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_area_served" className="text-sm font-medium">Area Served *</Label>
                <Input
                  id="edit_area_served"
                  value={editForm.area_served}
                  onChange={(e) => setEditForm({ ...editForm, area_served: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_status" className="text-sm font-medium">Status *</Label>
                <Select
                  value={editForm.status}
                  onValueChange={(value) => setEditForm({ ...editForm, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">🟢 Active</SelectItem>
                    <SelectItem value="maintenance">🔧 In Maintenance</SelectItem>
                    <SelectItem value="retired">⚪ Retired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_notes" className="text-sm font-medium">Notes</Label>
                <Textarea
                  id="edit_notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={3}
                />
              </div>
            </div>
            <div className="border-t bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-[#1E624A] hover:bg-[#164935]">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bus View Modal */}
      <Dialog open={isBusViewOpen} onOpenChange={setIsBusViewOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-[#1E624A] to-[#2a8a6a]">
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <BusIcon size={24} />
              Bus Details — {selectedBus?.bus_name}
            </DialogTitle>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
              <div className="text-white/90">
                <p className="text-xs text-white/70">📍 Area</p>
                <p className="font-medium">{selectedBus?.area_served}</p>
              </div>
              <div className="text-white/90">
                <p className="text-xs text-white/70">👨‍✈️ Driver</p>
                <p className="font-medium">{selectedBus?.driver_name}</p>
              </div>
              <div className="text-white/90">
                <p className="text-xs text-white/70">🚍 Capacity</p>
                <p className="font-medium">
                  {getStudentCount(selectedBus?.id)} / {selectedBus?.capacity}
                </p>
              </div>
              <div className="text-white/90">
                <p className="text-xs text-white/70">Status</p>
                <p className="font-medium capitalize">{selectedBus?.status}</p>
              </div>
            </div>
          </DialogHeader>

          {/* Tabs */}
          <div className="border-b bg-gray-50 px-6">
            <div className="flex gap-4">
              <button
                onClick={() => setActiveTab("students")}
                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "students"
                    ? "border-[#1E624A] text-[#1E624A]"
                    : "border-transparent text-gray-600 hover:text-gray-900"
                }`}
              >
                Students ({getStudentCount(selectedBus?.id)})
              </button>
              <button
                onClick={async () => {
                  setActiveTab("teachers");
                  // Refresh data when switching to teachers tab
                  await fetchData();
                  setForceUpdate(prev => prev + 1);
                }}
                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "teachers"
                    ? "border-[#1E624A] text-[#1E624A]"
                    : "border-transparent text-gray-600 hover:text-gray-900"
                }`}
              >
                Teachers ({getAssignedTeachers(selectedBus?.id).length})
              </button>
              <button
                onClick={() => setActiveTab("info")}
                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "info"
                    ? "border-[#1E624A] text-[#1E624A]"
                    : "border-transparent text-gray-600 hover:text-gray-900"
                }`}
              >
                Info
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === "students" && (
              <div>
                {getAssignedStudents(selectedBus?.id).length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Name</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Class</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">Session</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Guardian</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">Fee Type</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {getAssignedStudents(selectedBus?.id).map((student) => (
                          <tr key={student.id} className="hover:bg-gray-50">
                            <td className="px-3 py-3 text-sm font-medium text-gray-900">
                              {student.name}
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-700">
                              {getClassName(student.class_id)}
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full font-medium ${
                                student.session === "morning" 
                                  ? "bg-orange-100 text-orange-700" 
                                  : "bg-purple-100 text-purple-700"
                              }`}>
                                {student.session === "morning" ? "☀️ Morning" : "🌙 Afternoon"}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-700">
                              {student.guardian_name}
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full font-medium ${
                                student.fee_type === "full"
                                  ? "bg-green-100 text-green-700"
                                  : student.fee_type === "discounted"
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-blue-100 text-blue-700"
                              }`}>
                                {student.fee_type === "full" && "💰 Full"}
                                {student.fee_type === "discounted" && `💸 ${student.discount_percentage}% Off`}
                                {student.fee_type === "endorsed" && "🔵 Sponsored"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <BusIcon size={48} className="mx-auto mb-3 text-gray-300" />
                    <p className="text-lg font-medium">No students assigned to this bus yet</p>
                    <p className="text-sm mt-1">Students will appear here when they select this bus during enrollment</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "teachers" && (
              <div key={`teachers-${getAssignedTeachers(selectedBus?.id).length}-${forceUpdate}`}>
                <div className="mb-4 flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-[#1E624A]">Bus Teachers</h3>
                  <Button
                    onClick={() => setIsAssignTeacherOpen(true)}
                    className="bg-[#1E624A] hover:bg-[#164935] text-sm"
                    data-testid="assign-teacher-btn"
                  >
                    <UserCheck size={16} className="mr-2" />
                    Assign Teacher to Bus
                  </Button>
                </div>
                
                {getAssignedTeachers(selectedBus?.id).length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Teacher Name</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Classes/Subjects</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">Session</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">Role</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {getAssignedTeachers(selectedBus?.id).map((teacher) => (
                          <tr key={teacher.id} className="hover:bg-gray-50">
                            <td className="px-3 py-3 text-sm font-medium text-gray-900">
                              {teacher.name}
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-700">
                              <div className="flex flex-wrap gap-1">
                                {teacher.subjects?.map((subject) => (
                                  <span key={subject} className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                                    {subject}
                                  </span>
                                )) || "No subjects assigned"}
                              </div>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full font-medium ${
                                teacher.bus_session === "morning" 
                                  ? "bg-orange-100 text-orange-700" 
                                  : "bg-purple-100 text-purple-700"
                              }`}>
                                {teacher.bus_session === "morning" ? "🟡 Morning" : "🌙 Afternoon"}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full font-medium ${
                                teacher.bus_role === "supervisor" 
                                  ? "bg-green-100 text-green-700" 
                                  : "bg-teal-100 text-teal-700"
                              }`}>
                                {teacher.bus_role === "supervisor" ? "👨‍🏫 Supervisor" : "🤝 Escort"}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:bg-red-50 hover:border-red-500"
                                onClick={() => handleRemoveTeacherFromBus(teacher.id)}
                                data-testid={`remove-teacher-${teacher.id}-btn`}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <UserCheck size={48} className="mx-auto mb-3 text-gray-300" />
                    <p className="text-lg font-medium">No teachers assigned to this bus yet</p>
                    <p className="text-sm mt-1">Click "Assign Teacher to Bus" to add supervisors or escorts</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "info" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Bus Name</p>
                    <p className="font-medium text-gray-900">{selectedBus?.bus_name}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Plate Number</p>
                    <p className="font-medium text-gray-900">{selectedBus?.plate_number}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Driver Name</p>
                    <p className="font-medium text-gray-900">{selectedBus?.driver_name}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Capacity</p>
                    <p className="font-medium text-gray-900">{selectedBus?.capacity} seats</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Area Served</p>
                    <p className="font-medium text-gray-900">{selectedBus?.area_served}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Status</p>
                    <p className="font-medium text-gray-900 capitalize">{selectedBus?.status}</p>
                  </div>
                </div>
                {selectedBus?.notes && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-600 mb-2">Notes</p>
                    <p className="text-sm text-gray-700">{selectedBus.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t bg-gray-50 px-6 py-4 flex justify-end">
            <Button
              variant="outline"
              onClick={() => setIsBusViewOpen(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Teacher to Bus Modal */}
      <Dialog open={isAssignTeacherOpen} onOpenChange={setIsAssignTeacherOpen}>
        <DialogContent className="max-w-md pointer-events-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#1E624A] flex items-center gap-2">
              <UserCheck size={24} />
              Assign Teacher to Bus
            </DialogTitle>
            <p className="text-sm text-gray-600 mt-1">
              Assign a teacher as supervisor or escort for {selectedBus?.bus_name}
            </p>
          </DialogHeader>
          <form onSubmit={handleAssignTeacher} className="space-y-4">
            <div>
              <Label htmlFor="assign_teacher" className="text-sm font-medium">Select Teacher *</Label>
              <Select
                value={teacherAssignForm.teacher_id}
                onValueChange={(value) => setTeacherAssignForm({ ...teacherAssignForm, teacher_id: value })}
                required
              >
                <SelectTrigger data-testid="assign-teacher-select" className="pointer-events-auto">
                  <SelectValue placeholder="Choose a teacher..." />
                </SelectTrigger>
                <SelectContent className="pointer-events-auto z-[60]">
                  {teachers
                    .filter(teacher => {
                      // Filter active teachers only
                      const isActive = !teacher.hasOwnProperty('status') || teacher.status === 'active';
                      return isActive;
                    })
                    .sort((a, b) => (a.name || '').localeCompare(b.name || '')) // Sort by name ascending
                    .map((teacher) => {
                      // Format the display text cleanly
                      let displayText = `🧑‍🏫 ${teacher.name}`;
                      
                      // Add subject or class information
                      if (teacher.subjects && teacher.subjects.length > 0) {
                        // If teacher has subjects, show the first one
                        displayText += ` – ${teacher.subjects[0]}`;
                      } else if (teacher.assigned_classes && teacher.assigned_classes.length > 0) {
                        // If teacher has assigned classes, show the first one
                        displayText += ` – ${teacher.assigned_classes[0]}`;
                      } else if (teacher.className) {
                        // If teacher has a className field
                        displayText += ` – ${teacher.className}`;
                      } else if (teacher.subject) {
                        // If teacher has a single subject field
                        displayText += ` – ${teacher.subject}`;
                      }
                      
                      return (
                        <SelectItem key={teacher.id} value={teacher.id} className="pointer-events-auto">
                          {displayText}
                        </SelectItem>
                      );
                    })}
                  {teachers.filter(teacher => {
                    const isActive = !teacher.hasOwnProperty('status') || teacher.status === 'active';
                    return isActive;
                  }).length === 0 && (
                    <SelectItem value="" disabled className="pointer-events-auto">
                      No available teachers
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Only active teachers appear in this list
              </p>
            </div>
            
            <div>
              <Label htmlFor="assign_session" className="text-sm font-medium">Session *</Label>
              <Select
                value={teacherAssignForm.session}
                onValueChange={(value) => {
                  setTeacherAssignForm({ 
                    ...teacherAssignForm, 
                    session: value,
                    teacher_id: "" // Clear teacher selection when session changes
                  })
                }}
              >
                <SelectTrigger data-testid="assign-session-select" className="pointer-events-auto">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="pointer-events-auto z-[60]">
                  <SelectItem value="morning" className="pointer-events-auto">🟡 Morning Session</SelectItem>
                  <SelectItem value="afternoon" className="pointer-events-auto">🌙 Afternoon Session</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="assign_role" className="text-sm font-medium">Role *</Label>
              <Select
                value={teacherAssignForm.role}
                onValueChange={(value) => setTeacherAssignForm({ ...teacherAssignForm, role: value })}
              >
                <SelectTrigger data-testid="assign-role-select" className="pointer-events-auto">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="pointer-events-auto z-[60]">
                  <SelectItem value="supervisor" className="pointer-events-auto">👨‍🏫 Supervisor</SelectItem>
                  <SelectItem value="escort" className="pointer-events-auto">🤝 Escort</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsAssignTeacherOpen(false);
                  setTeacherAssignForm({ teacher_id: "", session: "morning", role: "supervisor" });
                }}
                data-testid="cancel-assign-teacher-btn"
                className="pointer-events-auto"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#1E624A] hover:bg-[#164935] pointer-events-auto"
                data-testid="confirm-assign-teacher-btn"
              >
                <UserCheck size={16} className="mr-2" />
                Assign Teacher
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Transport;
